// Theme presets for the GenUI dashboard panel. Each preset is a partial
// `Theme` (from @openuidev/react-ui) — overrides on top of the bundled
// `defaultDarkTheme` / `defaultLightTheme`. Null/undefined keys fall through
// to the OpenUI defaults so we don't have to redefine the full ~80-token
// shape per preset.
//
// The model picks a preset by name when calling `create_dashboard`; we
// resolve it at render time and feed it into `<ThemeProvider>` around the
// dashboard's `<Renderer>`. The resolved theme is also applied to the
// dashboard's *default chart palette* via `defaultChartPalette` plus the
// per-chart palette tokens — the per-chart `palette` arg the model can
// pass on each chart still wins.

import type { Theme } from '@openuidev/react-ui';

export type ThemePresetName =
  | 'galexia-violet'
  | 'ocean'
  | 'emerald'
  | 'sunset'
  | 'orchid'
  | 'mono'
  | 'paper';

export type ThemeMode = 'light' | 'dark';

export interface ThemeInput {
  /** Named preset. Defaults to "galexia-violet". */
  preset?: ThemePresetName;
  /** Override the accent color (button + link). OKLCH or hex. */
  accent?: string;
  /** Override the mode (most presets are dark). */
  mode?: ThemeMode;
  /** Free-form token overrides — keys must match `Theme`. Last-write-wins. */
  overrides?: Partial<Theme>;
}

interface ResolvedTheme {
  mode: ThemeMode;
  // Both lightTheme + darkTheme always set so ThemeProvider can switch
  // gracefully if we ever add a per-user mode toggle.
  lightTheme: Theme;
  darkTheme: Theme;
}

// Galexia's brand palette in OKLCH (matches apps/web/app/globals.css). Used
// as the default preset and as the basis for the dark variants.
const GALEXIA_VIOLET = 'oklch(0.64 0.22 272)';
const GALEXIA_VIOLET_HOVER = 'oklch(0.70 0.22 272)';
const GALEXIA_VIOLET_PRESSED = 'oklch(0.55 0.22 272)';

// Important: in OpenUI's vocabulary `foreground` is the ELEVATED surface
// used for tooltips, scroll buttons, and other popover-like overlays —
// NOT a text color. Setting it to a light value in dark mode makes
// tooltip backgrounds light while text stays light, which is exactly the
// "white on white" bug we hit on the first pass. Treat foreground as a
// surface tier between `background` (canvas) and `elevatedStrong`.
const presets: Record<ThemePresetName, { dark: Theme; light: Theme }> = {
  'galexia-violet': {
    dark: {
      background: 'oklch(0.10 0.05 275)',
      foreground: 'oklch(0.16 0.06 275)', // tooltip / popover surface
      elevated: 'oklch(0.13 0.06 275)',
      elevatedLight: 'oklch(0.16 0.06 275)',
      sunk: 'oklch(0.08 0.04 275)',
      sunkLight: 'oklch(0.11 0.05 275)',
      popoverBackground: 'oklch(0.16 0.06 275)',
      interactiveAccentDefault: GALEXIA_VIOLET,
      interactiveAccentHover: GALEXIA_VIOLET_HOVER,
      interactiveAccentPressed: GALEXIA_VIOLET_PRESSED,
      textBrand: 'oklch(0.78 0.18 272)',
      textAccentPrimary: 'oklch(0.82 0.16 272)',
      textNeutralPrimary: 'oklch(0.96 0.005 275)',
      textNeutralSecondary: 'oklch(0.78 0.02 275)',
      textNeutralTertiary: 'oklch(0.62 0.03 275)',
    },
    light: {
      background: 'oklch(0.98 0.005 275)',
      foreground: 'oklch(1 0 0)', // elevated white surface for tooltips
      elevated: 'oklch(1 0 0)',
      sunk: 'oklch(0.96 0.01 275)',
      popoverBackground: 'oklch(1 0 0)',
      interactiveAccentDefault: 'oklch(0.56 0.22 272)',
      textBrand: 'oklch(0.50 0.22 272)',
    },
  },
  ocean: {
    dark: {
      background: 'oklch(0.10 0.04 240)',
      foreground: 'oklch(0.16 0.05 240)',
      elevated: 'oklch(0.13 0.05 240)',
      sunk: 'oklch(0.08 0.04 240)',
      popoverBackground: 'oklch(0.16 0.05 240)',
      interactiveAccentDefault: 'oklch(0.62 0.16 240)',
      interactiveAccentHover: 'oklch(0.68 0.16 240)',
      textBrand: 'oklch(0.78 0.14 240)',
      textNeutralPrimary: 'oklch(0.96 0.005 240)',
      textNeutralSecondary: 'oklch(0.78 0.02 240)',
      textNeutralTertiary: 'oklch(0.62 0.03 240)',
    },
    light: {
      background: 'oklch(0.98 0.005 240)',
      foreground: 'oklch(1 0 0)',
      popoverBackground: 'oklch(1 0 0)',
      interactiveAccentDefault: 'oklch(0.55 0.18 240)',
      textBrand: 'oklch(0.45 0.18 240)',
    },
  },
  emerald: {
    dark: {
      background: 'oklch(0.10 0.04 165)',
      foreground: 'oklch(0.16 0.05 165)',
      elevated: 'oklch(0.13 0.05 165)',
      sunk: 'oklch(0.08 0.04 165)',
      popoverBackground: 'oklch(0.16 0.05 165)',
      interactiveAccentDefault: 'oklch(0.65 0.16 165)',
      interactiveAccentHover: 'oklch(0.71 0.16 165)',
      textBrand: 'oklch(0.80 0.14 165)',
      textNeutralPrimary: 'oklch(0.96 0.005 165)',
      textNeutralSecondary: 'oklch(0.78 0.02 165)',
      textNeutralTertiary: 'oklch(0.62 0.03 165)',
    },
    light: {
      background: 'oklch(0.98 0.005 165)',
      foreground: 'oklch(1 0 0)',
      popoverBackground: 'oklch(1 0 0)',
      interactiveAccentDefault: 'oklch(0.55 0.18 165)',
      textBrand: 'oklch(0.45 0.18 165)',
    },
  },
  sunset: {
    dark: {
      background: 'oklch(0.10 0.04 35)',
      foreground: 'oklch(0.17 0.05 35)',
      elevated: 'oklch(0.14 0.05 35)',
      sunk: 'oklch(0.08 0.04 35)',
      popoverBackground: 'oklch(0.17 0.05 35)',
      interactiveAccentDefault: 'oklch(0.68 0.18 35)',
      interactiveAccentHover: 'oklch(0.74 0.18 35)',
      textBrand: 'oklch(0.82 0.15 35)',
      textNeutralPrimary: 'oklch(0.96 0.005 35)',
      textNeutralSecondary: 'oklch(0.78 0.02 35)',
      textNeutralTertiary: 'oklch(0.62 0.03 35)',
    },
    light: {
      background: 'oklch(0.98 0.01 35)',
      foreground: 'oklch(1 0 0)',
      popoverBackground: 'oklch(1 0 0)',
      interactiveAccentDefault: 'oklch(0.60 0.20 35)',
      textBrand: 'oklch(0.50 0.20 35)',
    },
  },
  orchid: {
    dark: {
      background: 'oklch(0.10 0.04 320)',
      foreground: 'oklch(0.16 0.05 320)',
      elevated: 'oklch(0.13 0.05 320)',
      sunk: 'oklch(0.08 0.04 320)',
      popoverBackground: 'oklch(0.16 0.05 320)',
      interactiveAccentDefault: 'oklch(0.66 0.18 320)',
      interactiveAccentHover: 'oklch(0.72 0.18 320)',
      textBrand: 'oklch(0.80 0.15 320)',
      textNeutralPrimary: 'oklch(0.96 0.005 320)',
      textNeutralSecondary: 'oklch(0.78 0.02 320)',
      textNeutralTertiary: 'oklch(0.62 0.03 320)',
    },
    light: {
      background: 'oklch(0.98 0.005 320)',
      foreground: 'oklch(1 0 0)',
      popoverBackground: 'oklch(1 0 0)',
      interactiveAccentDefault: 'oklch(0.58 0.20 320)',
      textBrand: 'oklch(0.48 0.20 320)',
    },
  },
  mono: {
    dark: {
      background: 'oklch(0.11 0 0)',
      foreground: 'oklch(0.17 0 0)',
      elevated: 'oklch(0.14 0 0)',
      sunk: 'oklch(0.08 0 0)',
      popoverBackground: 'oklch(0.17 0 0)',
      interactiveAccentDefault: 'oklch(0.85 0 0)',
      interactiveAccentHover: 'oklch(0.92 0 0)',
      textBrand: 'oklch(0.92 0 0)',
      textNeutralPrimary: 'oklch(0.97 0 0)',
      textNeutralSecondary: 'oklch(0.78 0 0)',
      textNeutralTertiary: 'oklch(0.60 0 0)',
    },
    light: {
      background: 'oklch(0.99 0 0)',
      foreground: 'oklch(1 0 0)',
      popoverBackground: 'oklch(1 0 0)',
      interactiveAccentDefault: 'oklch(0.20 0 0)',
      textBrand: 'oklch(0.20 0 0)',
    },
  },
  paper: {
    dark: {
      // Paper preset is intentionally light; if forced dark, fall back to
      // a warm dim cream rather than pure black.
      background: 'oklch(0.18 0.01 80)',
      foreground: 'oklch(0.25 0.01 80)',
      elevated: 'oklch(0.22 0.01 80)',
      sunk: 'oklch(0.14 0.01 80)',
      popoverBackground: 'oklch(0.25 0.01 80)',
      interactiveAccentDefault: 'oklch(0.66 0.13 50)',
      textBrand: 'oklch(0.75 0.12 50)',
    },
    light: {
      background: 'oklch(0.98 0.012 85)',
      foreground: 'oklch(1 0.005 85)',
      elevated: 'oklch(1 0.005 85)',
      sunk: 'oklch(0.95 0.015 85)',
      popoverBackground: 'oklch(1 0.005 85)',
      interactiveAccentDefault: 'oklch(0.50 0.15 50)',
      textBrand: 'oklch(0.40 0.15 50)',
    },
  },
};

const DEFAULT_PRESET: ThemePresetName = 'galexia-violet';
const DEFAULT_MODE: ThemeMode = 'dark';

export function resolveTheme(input: ThemeInput | null | undefined): ResolvedTheme {
  const preset = (input?.preset && presets[input.preset] ? input.preset : DEFAULT_PRESET);
  const mode: ThemeMode = input?.mode ?? DEFAULT_MODE;
  const base = presets[preset];

  // Layer order (last wins): preset base → accent override → free-form overrides.
  const buildVariant = (variant: 'dark' | 'light'): Theme => {
    const layered: Theme = { ...base[variant] };
    if (input?.accent) {
      layered.interactiveAccentDefault = input.accent;
      // Don't try to derive hover/pressed — let those fall back to the
      // preset's values so the accent feels intentional rather than
      // poorly-derived.
    }
    if (input?.overrides) {
      Object.assign(layered, input.overrides);
    }
    return layered;
  };

  return {
    mode,
    darkTheme: buildVariant('dark'),
    lightTheme: buildVariant('light'),
  };
}

// JSON shape persisted on the `dashboards.theme` column. Keep separate from
// `ThemeInput` so we can validate at the API boundary without coupling the
// runtime resolver to Zod.
export interface PersistedTheme {
  preset?: ThemePresetName;
  accent?: string;
  mode?: ThemeMode;
  overrides?: Partial<Theme>;
}

export const PRESET_NAMES: ThemePresetName[] = [
  'galexia-violet',
  'ocean',
  'emerald',
  'sunset',
  'orchid',
  'mono',
  'paper',
];
